import {
  Box,
  Divider,
  Grid,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { getRazorPayPlan, getRazorPaySubId } from 'wisestep-api';
import {
  ControlledTextInput,
  Item,
  WsButton,
  WsColors,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import { useUserContext } from '../../../context/';
import { failed } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { Loader } from '../../auth/components/OtpPageLayout';
import { useCustomPlanStore } from '../store/customPlanStore';
import { IPaymentInfo } from '../types/IPaymentInfo';
import { IRazorpayEntPlanSub } from '../types/IRazorpayEntPlanSub';
import { getPlanCode, getRazorPayProductId } from '../utils/helper';

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: WsColors.TertiaryShades100,
    color: WsColors.GenericRichBlack,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const AlingTextSideBySide = ({
  leftText,
  rightText,
  leftTextCaption,
}: {
  leftText: string;
  rightText: string;
  leftTextCaption?: string;
}) => (
  <Grid container mt={2}>
    <Grid item xs={6} textAlign="left">
      <WsTypography bold> {leftText}</WsTypography>
      {leftTextCaption && (
        <WsTypography bold variant={WsTypoVariants.BODY2} color={WsTypoColors.QUINARY}>
          {leftTextCaption}
        </WsTypography>
      )}
    </Grid>
    <Grid item xs={6} textAlign="right">
      <WsTypography bold>{rightText}</WsTypography>
    </Grid>
    <Divider sx={{ width: '100%', mt: 1 }} />
  </Grid>
);

const getMonth = (time: string) => {
  if (time) {
    switch (time) {
      case 'month':
        return 1;
      case 'quater':
        return 3;
      case 'annual':
        return 12;
    }
  }
  return 1;
};

const RazorPayPaymentCalculation = ({
  plan,
  entPlanData,
}: {
  plan: IPaymentInfo;
  entPlanData: IRazorpayEntPlanSub | null;
}) => {
  const enableRazorPayEntPlan = useCustomPlanStore((state) => state.enableRazorPayEntPlan);
  const [loaded, setLoaded] = useState(false);
  const navigate = useNavigate();
  const loggedInUser = useUserContext();
  const planStartDate = dayjs(new Date()).format('MMM DD');
  const endDate = dayjs(new Date()).add(getMonth(plan?.time?.toString()), 'month');
  const planEndDate = dayjs(endDate).format('MMM DD');
  const [totalCharge, setTotalCharge] = useState(plan.totalPrice);
  const [subTotal, setSubTotal] = useState(
    parseFloat((plan.totalPrice - (plan.totalPrice * 18) / 100).toFixed(2))
  );
  const [tempUsers, setTempUsers] = useState(0);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const methods = useForm({
    defaultValues: {
      users: entPlanData?.users ? parseInt(entPlanData?.users) : 2,
    },
  });

  const razorPayProductId = getRazorPayProductId(plan.plan, plan.time);
  const { setValue, watch, getValues } = methods;

  const totalUsers = entPlanData?.users ? parseInt(entPlanData?.users) : watch('users');

  useEffect(() => {
    (async () => {
      const req = await getRazorPayPlan(razorPayProductId);
      const amount = req.data.item.amount / 100;
      plan.totalPrice = parseFloat(amount.toFixed(2));
      const total =
        parseFloat(amount.toFixed(2)) * (entPlanData?.users ? parseInt(entPlanData?.users) : 2);
      setTotalCharge(total);
      setSubTotal(parseFloat((total - (total * 18) / 100).toFixed(2)));
      setLoaded(true);
    })();
  }, [entPlanData?.users, plan, razorPayProductId]);

  useEffect(() => {
    if (totalUsers > 0 && totalUsers !== tempUsers) {
      setTempUsers(totalUsers);
      const perUserCharge = totalUsers * plan.totalPrice;
      setTotalCharge(perUserCharge);
      setSubTotal(parseFloat((perUserCharge - (perUserCharge * 18) / 100).toFixed(2)));
    }
  }, [plan.totalPrice, tempUsers, totalUsers]);

  const onInputChange = (e) => {
    const { value } = e.target;
    if (value < 2 && (!enableRazorPayEntPlan || totalUsers == 0)) {
      setNotification(failed('Users cannot be less then 2'));
      setValue('users', 2);
    }
  };

  const callRazorpay = (id: string) => {
    const rzp1 = new (window as any).Razorpay({
      key: process.env.REACT_APP_RAZORPAY_API_KEY,
      subscription_id: id,
      name: loggedInUser.userDTO?.fullName,
      description: `${plan.plan} Plan`,
      redirect: false,
      handler: function (response) {
        if (response.razorpay_payment_id) {
          navigate('/plan/purchase?success=true');
        } else {
          navigate('/plan/purchase?success=false');
        }
      },
      prefill: {
        name: loggedInUser.userDTO?.fullName,
        email: loggedInUser.userDTO?.email,
        contact: loggedInUser.userDTO?.phoneNumber,
      },
    });
    rzp1.open();
  };

  const onSubmit = async () => {
    const planInfo = getPlanCode(plan?.plan);
    if (enableRazorPayEntPlan && entPlanData?.id) {
      callRazorpay(entPlanData?.id);
    } else if (!isEmpty(planInfo)) {
      const req = await getRazorPaySubId({
        amount: plan.totalPrice,
        productId: razorPayProductId,
        interval: plan.time,
        customerId: plan.customerId,
        coupon: undefined,
        users: getValues('users')?.toString() ?? '0',
        manual: plan.manual,
        planCode: planInfo,
        userManagement: false,
      });
      callRazorpay(req.data);
    } else {
      setNotification(failed('Unable to detect plan'));
    }
  };

  return (
    <Grid container>
      {loaded ? (
        <FormProvider {...methods}>
          <Grid item xs={12} textAlign="center">
            <WsTypography variant={WsTypoVariants.TITLE} color={WsTypoColors.PRIMARY} bold>
              Your Cart
            </WsTypography>
          </Grid>
          <Grid item xs={12} mt={3}>
            <TableContainer>
              <Table sx={{ minWidth: 700 }} aria-label="customized table">
                <TableHead>
                  <TableRow>
                    <StyledTableCell>
                      <WsTypography bold>S.no</WsTypography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <WsTypography bold>Plan</WsTypography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <WsTypography bold>Price Per User</WsTypography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <WsTypography bold>Number of Users</WsTypography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <WsTypography bold>Total</WsTypography>
                    </StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      <WsTypography bold color={WsTypoColors.QUINARY}>
                        1.
                      </WsTypography>
                    </TableCell>
                    <TableCell component="th" scope="row">
                      <WsTypography bold color={WsTypoColors.QUINARY}>
                        {plan.plan}
                      </WsTypography>
                      <WsTypography bold color={WsTypoColors.QUINARY}>
                        {`${planStartDate} - ${planEndDate}, ${dayjs(endDate).format('YYYY')}`}
                      </WsTypography>
                    </TableCell>
                    <TableCell component="th" scope="row">
                      <WsTypography bold color={WsTypoColors.QUINARY}>
                        {plan.totalPrice}
                      </WsTypography>
                    </TableCell>
                    <TableCell component="th" scope="row">
                      <ControlledTextInput
                        onMouseLeave={onInputChange}
                        name="users"
                        labelText=""
                        type="number"
                        fullWidth
                        id="users"
                        placeholder=""
                        InputProps={{ inputProps: { min: 2 } }}
                        variant="outlined"
                        autoComplete="users"
                        disabled={enableRazorPayEntPlan}
                        sx={{ width: 150 }}
                      />
                    </TableCell>
                    <TableCell component="th" scope="row">
                      <WsTypography bold color={WsTypoColors.QUINARY}>
                        {`₹ ${(totalUsers * plan.totalPrice).toFixed(2)} including GST`}
                      </WsTypography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={8}>
            <Item sx={{ mt: 4, pb: 0 }}>
              <a href="https://razorpay.com/" target="_blank" rel="noreferrer">
                <img
                  src="https://badges.razorpay.com/badge-light.png"
                  style={{ height: '45px', width: '113px' }}
                  alt="Razorpay | Payment Gateway | Neobank"
                />
              </a>
            </Item>
          </Grid>
          <Grid item xs={4} mt={3}>
            <AlingTextSideBySide leftText="Subtotal:" rightText={`₹ ${subTotal}`} />
            {plan.sameStateGST ? (
              <>
                <AlingTextSideBySide leftText="CGST" rightText={`9%`} />
                <AlingTextSideBySide leftText="SGST" rightText={`9%`} />
              </>
            ) : (
              <AlingTextSideBySide leftText="IGST" rightText={`18%`} />
            )}
            <AlingTextSideBySide
              leftText="Grand Total:"
              rightText={
                totalCharge
                  ? Number.isInteger(totalCharge)
                    ? `₹ ${totalCharge?.toString()}.00`
                    : `₹ ${totalCharge?.toFixed(2)?.toString()}`
                  : '00.00'
              }
            />
            <Stack mt={2}>
              <WsButton
                size="large"
                disabled={totalUsers < 2 && (!enableRazorPayEntPlan || totalUsers == 0)}
                onClick={onSubmit}
              >
                Pay Now
              </WsButton>
            </Stack>
          </Grid>
        </FormProvider>
      ) : (
        <Box sx={{ height: '400px' }}>
          <Loader />
        </Box>
      )}
    </Grid>
  );
};

export default RazorPayPaymentCalculation;
